import * as React from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SearchIcon from "@mui/icons-material/Search";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { createLocation, deleteLocation } from "../actions/location";

const columns = [{ id: "name", label: "NAME", minWidth: 200 }];

const colors = {
  primary: "#21313c",
  primaryLight: "#304753",
  primaryDark: "#19262f",
  secondary: "#17b859",
  secondaryDark: "#109347",
  secondarySoft: "rgba(23, 184, 89, 0.12)",
  background: "#f6f8fa",
  surface: "#ffffff",
  surfaceSoft: "#f8fafc",
  border: "#e4e9ee",
  textPrimary: "#21313c",
  textSecondary: "#667085",
  danger: "#d32f2f",
  dangerSoft: "rgba(211, 47, 47, 0.08)",
};

export default function Location() {
  const dispatch = useDispatch();
  const locations = useSelector((state) => state.location);

  const [data, setData] = React.useState(locations);
  const [search, setSearch] = React.useState("");
  const [postData, setPostData] = React.useState({ name: "" });

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [deleteText, setDeleteText] = React.useState("");
  const [selectedLocation, setSelectedLocation] = React.useState(null);

  React.useEffect(() => {
    setData(locations);
  }, [locations]);

  const filteredData = React.useMemo(() => {
    return data.filter((item) =>
      item?.name?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [data, search]);

  const rows = React.useMemo(
    () =>
      filteredData.map((item) => ({
        id: item._id,
        name: item.name,
      })),
    [filteredData],
  );

  const sendData = () => {
    const trimmedName = postData.name.trim().toLowerCase();

    if (!trimmedName) {
      toast.error("Location field is required");
      return;
    }

    const alreadyExists = locations.some(
      (item) => item?.name?.trim().toLowerCase() === trimmedName,
    );

    if (alreadyExists) {
      toast.error("Location already exists");
      return;
    }

    dispatch(createLocation({ name: trimmedName }));
    setPostData({ name: "" });
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedLocation(row);
    setDeleteText("");
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedLocation(null);
    setDeleteText("");
    setOpenDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    if (deleteText !== "DELETE") {
      toast.error('Please type "DELETE" to continue');
      return;
    }

    if (selectedLocation?.id) {
      dispatch(deleteLocation(selectedLocation.id));
      toast.success("Location deleted successfully");
    }

    handleCloseDeleteModal();
  };

  return (
    <Box>
      <Helmet>
        <title>Locations | Reconciliation</title>
      </Helmet>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              fullWidth
              required
              value={postData.name}
              onChange={(e) =>
                setPostData({
                  ...postData,
                  name: e.target.value.toLowerCase(),
                })
              }
              size="small"
              label="Location"
              placeholder="Enter location name"
              onKeyDown={(e) => {
                if (e.key === "Enter") sendData();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceSoft,
                  "&.Mui-focused fieldset": {
                    borderColor: colors.secondary,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.secondary,
                },
              }}
            />

            <Button
              size="small"
              onClick={sendData}
              variant="contained"
              startIcon={<AddLocationAltOutlinedIcon />}
              sx={{
                minWidth: { xs: "100%", md: 170 },
                height: 40,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
                boxShadow: "0 10px 18px rgba(23, 184, 89, 0.22)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #109347 0%, #0d7a3b 100%)",
                },
              }}
            >
              Add Location
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                All locations
              </Typography>
              <Chip
                size="small"
                label={rows.length}
                sx={{ fontWeight: 700, backgroundColor: colors.secondarySoft }}
              />
            </Stack>

            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              label="Search location"
              placeholder="Type to search..."
              sx={{
                minWidth: { xs: "100%", md: 300 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceSoft,
                  "&.Mui-focused fieldset": {
                    borderColor: colors.secondary,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.secondary,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <TableContainer
            sx={{
              maxHeight: "calc(100dvh - 270px)",
              borderRadius: "14px",
              border: "1px solid #edf2f7",
            }}
          >
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        minWidth: column.minWidth,
                        backgroundColor: colors.primary,
                        color: "#fff",
                        fontWeight: 700,
                        borderBottom: "none",
                        py: 1.8,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                  <TableCell
                    sx={{
                      backgroundColor: colors.primary,
                      color: "#fff",
                      fontWeight: 700,
                      borderBottom: "none",
                      width: 120,
                      py: 1.8,
                    }}
                  >
                    ACTION
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "#fcfdff",
                        },
                        "&:hover": {
                          backgroundColor: "#f5f8fb",
                        },
                      }}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              py: 1.6,
                              color: "#344054",
                              fontWeight: 500,
                            }}
                          >
                            {value}
                          </TableCell>
                        );
                      })}

                      <TableCell sx={{ py: 1 }}>
                        <Tooltip title="Delete location">
                          <IconButton
                            onClick={() => handleOpenDeleteModal(row)}
                            sx={{
                              color: colors.danger,
                              backgroundColor: colors.dangerSoft,
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.16)",
                              },
                            }}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 7 }}>
                      <Stack spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "20px",
                            background: colors.secondarySoft,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PlaceOutlinedIcon
                            sx={{ fontSize: 38, color: colors.secondary }}
                          />
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 700, color: "#344054" }}
                        >
                          No locations found
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: colors.textSecondary }}
                        >
                          Try adding a new location or adjusting your search.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <Dialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberRoundedIcon />
          Confirm Deletion
        </DialogTitle>

        <DialogContent sx={{ pt: 3, mt: 1.5 }}>
          <Stack spacing={2}>
            <Typography sx={{ color: "#344054" }}>
              You are about to delete this location:
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: "12px",
                backgroundColor: colors.surfaceSoft,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                {selectedLocation?.name || ""}
              </Typography>
            </Paper>

            <Typography sx={{ color: colors.textSecondary, lineHeight: 1.7 }}>
              To continue, type <strong>DELETE</strong> in the field below.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label='Type "DELETE" to confirm'
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceSoft,
                  "&.Mui-focused fieldset": {
                    borderColor: colors.danger,
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.danger,
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCloseDeleteModal}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#344054",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={deleteText !== "DELETE"}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              backgroundColor: colors.danger,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
              "&.Mui-disabled": {
                backgroundColor: "#f1f3f5",
                color: "#98a2b3",
              },
            }}
          >
            Delete Location
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
